/**
 * Fondi Pensione — Pensione Reale Classic & Premium (Reale Mutua).
 * Pagina hub: vantaggi previdenza complementare, calcolatore gap, prodotti, CTA.
 *
 * Conformità:
 *  - Reg. IVASS 40/2018 (questionario IDD demand & needs)
 *  - D.Lgs. 252/2005 art. 8 (deduzione fino € 5.300/anno — L. Bilancio 2026)
 *  - Extra-deducibilità neo-assunti post-2007: fino € 7.950/anno per 20 anni
 *  - POG IDD: target market positivo/negativo dichiarato in pagina
 *  - Distributore: Christian e Ciro Colucci S.A.S. — RUI A000464752
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  PiggyBank, TrendingDown, Calculator, Shield, FileText, ArrowRight,
  CheckCircle, AlertTriangle, Info, Heart, Briefcase, Users, GraduationCap,
  Building2, Scale, Sparkles, FileCheck, Download, Phone,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer,
  CartesianGrid, Legend,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (n) => "€ " + (n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 });
const fmtEurCompat = (n) => {
  const v = Number(n || 0);
  if (v >= 1000) return "€ " + (v / 1000).toFixed(1) + "k";
  return "€ " + v.toFixed(0);
};

// =============================================================================
// HUB FONDI PENSIONE
// =============================================================================
export default function FondiPensionePage() {
  const [products, setProducts] = useState(null);
  const [docs, setDocs] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${API}/fondi-pensione/products`).then(r => setProducts(r.data?.products)).catch(() => {});
    axios.get(`${API}/fondi-pensione/documents`).then(r => setDocs(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#F5F4F0]">
      <Header />

      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#0F2F1F] via-[#163321] to-[#1d4530] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 mix-blend-soft-light" style={{
          backgroundImage: "radial-gradient(circle at 20% 30%, #D97B58 0%, transparent 50%), radial-gradient(circle at 80% 70%, #FFD089 0%, transparent 50%)"
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFD089] font-semibold mb-3">Fondi Pensione · PIP Reale Mutua</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            Costruisci la tua <em className="not-italic text-[#FFD089]">seconda pensione</em> oggi.
          </h1>
          <p className="mt-5 text-lg text-white/75 max-w-2xl leading-relaxed">
            La sola pensione INPS coprirà solo il 50-60% dell'ultimo stipendio. Pensione Reale Classic e Premium di Reale Mutua ti aiutano a colmare il gap, con il vantaggio fiscale di dedurre fino a <strong>€ 5.300 all'anno</strong> dal reddito imponibile (Legge di Bilancio 2026).
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="hero-cta">
            <button onClick={() => document.getElementById("calc")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center justify-center h-12 px-6 rounded-full bg-[#FFD089] text-[#0F2F1F] font-semibold text-sm hover:bg-[#FFDFA0] transition" data-testid="hero-calc">
              <Calculator className="w-4 h-4 mr-2" /> Calcola il tuo gap pensionistico
            </button>
            <Link to="/fondi-pensione/questionario" className="inline-flex items-center justify-center h-12 px-6 rounded-full bg-white text-[#0F2F1F] font-semibold text-sm hover:bg-white/90 transition" data-testid="hero-questionario">
              Verifica adeguatezza prodotto <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
          <p className="mt-6 text-[11px] text-white/50 max-w-2xl">
            Distribuito da Christian e Ciro Colucci S.A.S. — Intermediario assicurativo iscritto IVASS sez. A — RUI A000464752 · In partnership con Reale Mutua Assicurazioni.
          </p>
        </div>
      </section>

      {/* PERCHÉ — vantaggi */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading text-3xl text-[#163321] mb-2">Perché un fondo pensione integrativo?</h2>
        <p className="text-sm text-[#4A5E51] mb-8 max-w-2xl">L'INPS calcola la pensione con il sistema contributivo: chi inizia a lavorare oggi avrà solo il 50-60% dell'ultimo stipendio. La previdenza complementare colma questo gap.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <BenefitCard Icon={Scale} title="Deduzione fiscale" desc="Fino a € 5.300/anno dal reddito IRPEF (art. 8 D.Lgs. 252/2005 — L. Bilancio 2026). Extra-deducibilità € 7.950/anno per neo-assunti post-2007." />
          <BenefitCard Icon={TrendingDown} title="Gap previdenziale" desc="Colmi il divario tra pensione INPS e tenore di vita desiderato (80% reddito attuale)." />
          <BenefitCard Icon={Shield} title="Tutela del capitale" desc="Linea Garantita: capitale conservato. Linee Unit-Linked: rendimenti potenziali maggiori." />
          <BenefitCard Icon={FileCheck} title="Liquidità anticipata" desc="Anticipazioni ammesse per spese sanitarie, prima casa, formazione. Non è un capitale 'bloccato' fino alla pensione." />
        </div>
      </section>

      {/* CALCOLATORE GAP */}
      <Calcolatore />

      {/* PRODOTTI */}
      {products && <ProdottiSection products={products} />}

      {/* DOCUMENTI */}
      {docs && <DocumentiSection docs={docs} />}

      {/* COMPLIANCE */}
      <ComplianceSection />

      {/* CTA finale */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <div className="bg-gradient-to-br from-[#163321] to-[#0F2F1F] rounded-3xl p-8 sm:p-12 text-white text-center">
          <h2 className="font-heading text-3xl sm:text-4xl mb-3">Pronto a costruire la tua pensione?</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-6 text-sm">Compila il questionario IDD: in 4 minuti scopri quale prodotto è più adatto a te. Un consulente Colucci ti chiamerà entro 24h per la consulenza personalizzata.</p>
          <Link to="/fondi-pensione/questionario" className="inline-flex items-center h-12 px-6 rounded-full bg-[#FFD089] text-[#0F2F1F] font-semibold text-sm hover:bg-[#FFDFA0] transition" data-testid="cta-final">
            Inizia il questionario <ArrowRight className="w-4 h-4 ml-2" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function BenefitCard({ Icon, title, desc }) {
  return (
    <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 hover:border-[#163321] hover:shadow-md transition-all">
      <div className="w-10 h-10 rounded-xl bg-[#163321] text-white flex items-center justify-center mb-3"><Icon className="w-5 h-5" /></div>
      <p className="font-semibold text-[#163321] text-base">{title}</p>
      <p className="text-xs text-[#4A5E51] mt-1.5 leading-relaxed">{desc}</p>
    </div>
  );
}

// =============================================================================
// CALCOLATORE GAP PREVIDENZIALE — "Consulente Reale Mutua" (Fase 5-ter)
// =============================================================================
function Calcolatore() {
  // Input base
  const [eta, setEta] = useState(35);
  const [sesso, setSesso] = useState("M");
  const [reddito, setReddito] = useState(30000);
  const [anniContrib, setAnniContrib] = useState(10);
  const [contribMese, setContribMese] = useState(100);
  // Fase 5
  const [etaPens, setEtaPens] = useState(67);
  const [tenoreVita, setTenoreVita] = useState(80);
  const [conferiscoTfr, setConferiscoTfr] = useState(false);
  const [categoria, setCategoria] = useState("dipendente_privato");
  // Fase 5-bis
  const [neoAssunto, setNeoAssunto] = useState(false);
  // Fase 5-ter — Reale Mutua consulente
  const [capitalePresente, setCapitalePresente] = useState(0);
  const [prodotto, setProdotto] = useState("pensione_reale_classic");
  const [comparto, setComparto] = useState("gestione_separata_previdenza_reale");
  const [modalita, setModalita] = useState("capitale_50_rendita_50");
  const [anniIscrizione, setAnniIscrizione] = useState(5);

  const [catalogo, setCatalogo] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  // Carica catalogo prodotti Reale Mutua
  useEffect(() => {
    axios.get(`${API}/fondi-pensione/catalogo-reale-mutua`)
      .then(r => setCatalogo(r.data))
      .catch(() => {});
  }, []);

  // Se l'utente cambia prodotto, resetta comparto sul primo disponibile
  useEffect(() => {
    if (!catalogo) return;
    const prod = catalogo.prodotti[prodotto];
    if (!prod) return;
    const ids = Object.keys(prod.comparti || {});
    if (ids.length && !ids.includes(comparto)) {
      setComparto(ids[0]);
    }
    // eslint-disable-next-line
  }, [prodotto, catalogo]);

  const comparti = useMemo(() => {
    if (!catalogo) return {};
    return catalogo.prodotti?.[prodotto]?.comparti || {};
  }, [catalogo, prodotto]);

  const calc = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/fondi-pensione/calcolatore`, {
        eta, sesso,
        reddito_lordo_annuo: reddito,
        anni_contributivi: anniContrib,
        contributo_mensile: contribMese,
        eta_pensionabile: etaPens,
        tenore_vita_target: tenoreVita / 100,
        conferisco_tfr: conferiscoTfr,
        categoria,
        neo_assunto_post_2007: neoAssunto,
        capitale_gia_presente: capitalePresente,
        prodotto_reale_mutua: prodotto,
        comparto,
        modalita_erogazione: modalita,
        anni_iscrizione_fondo: anniIscrizione,
      });
      setResult(data);
    } catch {
      toast.error("Errore nel calcolo. Riprova.");
    } finally { setLoading(false); }
  };

  useEffect(() => { calc(); /* eslint-disable-next-line */ }, [
    eta, sesso, reddito, anniContrib, contribMese, etaPens, tenoreVita,
    conferiscoTfr, categoria, neoAssunto, capitalePresente, prodotto,
    comparto, modalita, anniIscrizione,
  ]);

  const isTfrApplicable = ["dipendente_privato", "dirigente", "pubblico"].includes(categoria);
  const pp = result?.pensione_pubblica;
  const sf = result?.simulazione_fondo;
  const gap = result?.gap;
  const fisc = result?.fisco;
  const coperto = sf?.gap_coperto;

  return (
    <section id="calc" className="bg-white border-y border-[#E5E2DB]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
        <h2 className="font-heading text-3xl text-[#163321] mb-2">Calcolatore consulente Reale Mutua</h2>
        <p className="text-sm text-[#4A5E51] mb-8 max-w-2xl">
          Simula il gap pensionistico e scopri come colmarlo con i prodotti Reale Mutua distribuiti da Colucci Assicuratori.
          Calcoli conformi a Legge di Bilancio 2026 (deduzione € 5.300, IRPEF 23/33/43%, aliquota rendita 15% → 9%).
        </p>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-8" data-testid="calculator">
          {/* ======================== INPUT ======================== */}
          <div className="space-y-5">
            <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5E51]">1. Dati anagrafici</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-semibold text-[#163321] mb-1.5 uppercase tracking-wide">Sesso</label>
                <select
                  value={sesso}
                  onChange={(e) => setSesso(e.target.value)}
                  className="w-full border border-[#E5E2DB] rounded-xl px-3 py-2.5 text-sm font-medium bg-white focus:border-[#163321] focus:outline-none"
                  data-testid="calc-sesso"
                >
                  <option value="M">Uomo</option>
                  <option value="F">Donna</option>
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-semibold text-[#163321] mb-1.5 uppercase tracking-wide">Categoria</label>
                <select
                  value={categoria}
                  onChange={(e) => setCategoria(e.target.value)}
                  className="w-full border border-[#E5E2DB] rounded-xl px-3 py-2.5 text-sm font-medium bg-white focus:border-[#163321] focus:outline-none"
                  data-testid="calc-categoria"
                >
                  <option value="dipendente_privato">Dipendente privato</option>
                  <option value="dirigente">Dirigente</option>
                  <option value="pubblico">Dipendente pubblico</option>
                  <option value="autonomo">Autonomo / Partita IVA</option>
                  <option value="gestione_separata">Gestione Separata</option>
                </select>
              </div>
            </div>
            <SliderField label="Età attuale" value={eta} setValue={setEta} min={18} max={65} unit="anni" testid="calc-eta" />
            <SliderField label="Anni di contributi INPS già versati" value={anniContrib} setValue={setAnniContrib} min={0} max={45} unit="anni" testid="calc-anni" />

            <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5E51] pt-3 border-t border-[#E5E2DB]">2. Situazione finanziaria</p>
            <SliderField label="Reddito lordo annuo" value={reddito} setValue={setReddito} min={10000} max={150000} step={1000} unit="€" testid="calc-reddito" />
            <SliderField label="Quanto puoi versare al mese?" value={contribMese} setValue={setContribMese} min={0} max={1000} step={10} unit="€" testid="calc-contrib" />
            <SliderField label="Capitale già presente nel fondo pensione" value={capitalePresente} setValue={setCapitalePresente} min={0} max={200000} step={500} unit="€" testid="calc-capitale-pres" hint="Es. trasferimento da altro fondo" />
            <SliderField label="Anni già iscritto a un fondo pensione" value={anniIscrizione} setValue={setAnniIscrizione} min={0} max={40} unit="anni" testid="calc-anni-isc" hint="Impatta l'aliquota di tassazione della futura rendita (15% → 9%)" />

            <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5E51] pt-3 border-t border-[#E5E2DB]">3. Pensione</p>
            <SliderField label="Età pensionabile prevista" value={etaPens} setValue={setEtaPens} min={60} max={71} unit="anni" testid="calc-eta-pens" />
            <SliderField label="Tenore di vita desiderato" value={tenoreVita} setValue={setTenoreVita} min={60} max={90} step={5} unit="%" testid="calc-tenore" hint="OECD raccomanda 70-80%" />

            <p className="text-[10px] uppercase tracking-widest font-bold text-[#4A5E51] pt-3 border-t border-[#E5E2DB]">4. Prodotto Reale Mutua</p>
            <div>
              <label className="block text-[11px] font-semibold text-[#163321] mb-1.5 uppercase tracking-wide">Prodotto</label>
              <select
                value={prodotto}
                onChange={(e) => setProdotto(e.target.value)}
                className="w-full border border-[#E5E2DB] rounded-xl px-3 py-2.5 text-sm font-medium bg-white focus:border-[#163321] focus:outline-none"
                data-testid="calc-prodotto"
              >
                <option value="pensione_reale_classic">Pensione Reale Classic (PIP)</option>
                <option value="pensione_reale_premium">Pensione Reale Premium (PIP + LTC)</option>
                <option value="fondo_teseo">Fondo Pensione Aperto Teseo (etico SRI)</option>
              </select>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#163321] mb-1.5 uppercase tracking-wide">Comparto di investimento</label>
              <div className="space-y-1.5">
                {Object.entries(comparti).map(([cid, c]) => (
                  <label
                    key={cid}
                    className={`flex items-start gap-2 p-2.5 rounded-xl border cursor-pointer transition-colors ${
                      comparto === cid ? "border-[#163321] bg-[#E6F4EA]" : "border-[#E5E2DB] bg-white hover:border-[#163321]/50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="comparto"
                      checked={comparto === cid}
                      onChange={() => setComparto(cid)}
                      className="mt-1 accent-[#163321]"
                      data-testid={`calc-comp-${cid}`}
                    />
                    <div className="flex-1">
                      <p className="text-[13px] font-semibold text-[#0F1F14]">{c.nome}</p>
                      <p className="text-[10px] text-[#4A5E51]">
                        Rischio: <strong>{c.rischio_label}</strong> · Rendimento netto ipotetico: <strong>{(c.rendimento_ipotetico_netto * 100).toFixed(1)}%</strong> · Orizzonte {c.orizzonte_anni} anni
                        {c.min_garantito_capitale && <span className="ml-1 text-emerald-700">· Capitale garantito</span>}
                        {c.esg && <span className="ml-1 text-emerald-700">· ESG</span>}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-[#163321] mb-1.5 uppercase tracking-wide">Modalità erogazione al pensionamento</label>
              <select
                value={modalita}
                onChange={(e) => setModalita(e.target.value)}
                className="w-full border border-[#E5E2DB] rounded-xl px-3 py-2.5 text-sm font-medium bg-white focus:border-[#163321] focus:outline-none"
                data-testid="calc-modalita"
              >
                <option value="capitale_50_rendita_50">50% capitale una tantum + 50% rendita vitalizia</option>
                <option value="rendita_100">100% rendita vitalizia mensile</option>
                <option value="rita">RITA — Rendita Integrativa Temporanea Anticipata (5 anni)</option>
              </select>
            </div>

            {/* TFR */}
            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              conferiscoTfr ? "border-[#163321] bg-[#E6F4EA]" : "border-[#E5E2DB] bg-white hover:border-[#163321]/50"
            } ${!isTfrApplicable ? "opacity-50 cursor-not-allowed" : ""}`}>
              <input type="checkbox" checked={conferiscoTfr && isTfrApplicable} disabled={!isTfrApplicable} onChange={(e) => setConferiscoTfr(e.target.checked)} className="mt-0.5 accent-[#163321]" data-testid="calc-tfr" />
              <div>
                <p className="text-sm font-semibold text-[#0F1F14]">Voglio conferire il TFR al Fondo Pensione</p>
                <p className="text-[11px] text-[#4A5E51] mt-0.5">
                  {isTfrApplicable
                    ? "6,91% del lordo annuo (art. 2120 c.c.). Il TFR non rientra nel limite di deducibilità annuo."
                    : "Disponibile solo per dipendenti privati, pubblici o dirigenti."}
                </p>
              </div>
            </label>

            {/* Neo-assunto */}
            <label className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
              neoAssunto ? "border-emerald-600 bg-emerald-50" : "border-[#E5E2DB] bg-white hover:border-[#163321]/50"
            }`}>
              <input type="checkbox" checked={neoAssunto} onChange={(e) => setNeoAssunto(e.target.checked)} className="mt-0.5 accent-emerald-700" data-testid="calc-neo-assunto" />
              <div>
                <p className="text-sm font-semibold text-[#0F1F14]">Neo-assunto (prima occupazione dopo il 1/1/2007)</p>
                <p className="text-[11px] text-[#4A5E51] mt-0.5">
                  Deducibilità fino a <strong>€ 7.950/anno</strong> nei primi 20 anni (art. 8 c.6 D.Lgs. 252/2005).
                </p>
              </div>
            </label>
          </div>

          {/* ======================== OUTPUT ======================== */}
          <div className="bg-[#F5F4F0] rounded-2xl p-6 space-y-4">
            {loading || !result ? (
              <p className="text-center text-[#4A5E51] py-12">Calcolo in corso…</p>
            ) : (
              <>
                {/* Badge copertura gap */}
                {gap?.mensile_lordo > 0 && (
                  <div className={`rounded-xl p-4 border-2 ${
                    coperto
                      ? "bg-emerald-50 border-emerald-600"
                      : "bg-amber-50 border-amber-500"
                  }`} data-testid="copertura-badge">
                    <div className="flex items-center gap-2 mb-1">
                      {coperto
                        ? <CheckCircle className="w-5 h-5 text-emerald-700" />
                        : <AlertTriangle className="w-5 h-5 text-amber-700" />
                      }
                      <p className={`text-[11px] uppercase tracking-wider font-bold ${
                        coperto ? "text-emerald-800" : "text-amber-800"
                      }`}>
                        {coperto ? "Gap coperto" : "Gap parzialmente coperto"}
                      </p>
                    </div>
                    <p className={`text-3xl font-bold ${coperto ? "text-emerald-700" : "text-amber-700"}`}>
                      {sf?.copertura_gap_percentuale}%
                    </p>
                    <p className="text-[11px] mt-1 text-[#4A5E51]">
                      {coperto
                        ? `La tua rendita Reale Mutua copre il 100% del gap (e anche oltre).`
                        : `Manca il ${(100 - (sf?.copertura_gap_percentuale || 0)).toFixed(0)}% — aumenta il contributo o scegli un comparto con orizzonte più lungo.`
                      }
                    </p>
                  </div>
                )}

                {/* Pensione pubblica + target */}
                <div className="grid grid-cols-2 gap-3">
                  <ResultBox value={fmtEur(pp?.stima_mensile_lorda)} label="Pensione INPS stimata/mese" color="amber" />
                  <ResultBox value={fmtEur(result.tenore_vita_target?.reddito_mensile_target)} label={`Tenore desiderato (${result.tenore_vita_target?.percentuale_pct}%)`} color="neutral" />
                </div>

                {/* Meta info */}
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                  <span className="px-2 py-0.5 rounded-full bg-[#E5E2DB] text-[#4A5E51] font-semibold uppercase tracking-wide">
                    {pp?.regime === "contributivo_puro" ? "Contributivo puro" : pp?.regime === "misto" ? "Misto" : "Retributivo"}
                  </span>
                  <span className="px-2 py-0.5 rounded-full bg-[#E5E2DB] text-[#4A5E51]">TS INPS: <strong>{pp?.tasso_sostituzione_usato_pct}%</strong></span>
                  <span className="px-2 py-0.5 rounded-full bg-[#E5E2DB] text-[#4A5E51]" title="Coefficiente trasformazione INPS (pensione pubblica)">Coeff. INPS: <strong>{pp?.coefficiente_inps_pct}%</strong></span>
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800" title="Coefficiente rendita FP (ANIA IPS55)">Coeff. rendita FP: <strong>{sf?.coefficiente_rendita_pct}%</strong></span>
                </div>

                {/* GAP */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="gap-result">
                  <p className="text-[10px] uppercase tracking-wider text-red-700 font-bold mb-1">Gap previdenziale mensile</p>
                  <p className="text-3xl font-bold text-red-700">{fmtEur(gap?.mensile_lordo)}</p>
                  <p className="text-[11px] text-red-600/80 mt-1">
                    {gap?.mensile_lordo > 0
                      ? `Servono ${fmtEur(sf?.capitale_necessario_per_coprire_gap)} di montante per generare rendita equivalente al gap.`
                      : "Nessun gap rilevato."}
                  </p>
                </div>

                {/* MONTANTE + prestazioni */}
                {(contribMese > 0 || capitalePresente > 0) && sf && (
                  <div className="bg-[#E6F4EA] border border-[#163321]/30 rounded-xl p-4 space-y-2" data-testid="capitale-result">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-[#163321] font-bold mb-1">
                          Montante a {etaPens} anni — {sf.prodotto_nome}
                        </p>
                        <p className="text-[10px] text-[#4A5E51]">
                          Comparto {sf.comparto_nome} — rendimento ipotetico {sf.rendimento_netto_applicato_pct}% netto
                        </p>
                      </div>
                      <p className="text-3xl font-bold text-[#163321]">{fmtEur(sf.montante_al_pensionamento)}</p>
                    </div>

                    <div className="text-[11px] text-[#163321]/80 space-y-0.5 pt-1 border-t border-[#163321]/20">
                      {sf.contributi_volontari_totali > 0 &&
                        <p>· Da versamenti volontari: <strong>{fmtEur(sf.contributi_volontari_totali)}</strong></p>}
                      {sf.tfr_conferito_totale > 0 &&
                        <p>· Da TFR conferito: <strong>{fmtEur(sf.tfr_conferito_totale)}</strong> ({fmtEur(sf.tfr_annuo_conferito)}/anno)</p>}
                      {sf.capitale_preesistente_rivalutato > 0 &&
                        <p>· Da capitale preesistente rivalutato: <strong>{fmtEur(sf.capitale_preesistente_rivalutato)}</strong></p>}
                      <p>· Di cui rendimenti generati: <strong>{fmtEur(sf.rendimenti_generati)}</strong></p>
                    </div>

                    {/* Prestazioni erogate */}
                    <div className="pt-2 border-t border-[#163321]/20 space-y-1">
                      <p className="text-[10px] uppercase tracking-wider text-[#163321] font-bold">Erogazione — {sf.modalita_erogazione_label}</p>
                      {sf.capitale_erogato_una_tantum_netto > 0 && (
                        <p className="text-[11px] text-[#163321]/80">· Capitale una tantum netto: <strong>{fmtEur(sf.capitale_erogato_una_tantum_netto)}</strong></p>
                      )}
                      <p className="text-[11px] text-[#163321]/80">· Rendita mensile netta: <strong>{fmtEur(sf.rendita_netta_mensile)}/mese</strong> (aliquota {sf.aliquota_tassazione_rendita_pct}%)</p>
                    </div>

                    {/* Vantaggio fiscale */}
                    {contribMese > 0 && fisc && (
                      <p className="pt-2 border-t border-[#163321]/20 text-[11px]">
                        + <strong className="text-[#163321]">{fmtEur(fisc.risparmio_fiscale_annuo)}/anno</strong> di risparmio fiscale
                        <span className="text-[#163321]/60"> · IRPEF marginale {fisc.aliquota_irpef_marginale_pct}% su dedotto max {fmtEur(fisc.deduzione_max_applicabile)}</span>
                      </p>
                    )}
                    {fisc?.bonus_neo_assunto_applicato && (
                      <p className="text-[11px] text-emerald-800 font-semibold">{fisc.messaggio_extra_deducibilita}</p>
                    )}
                  </div>
                )}

                {/* Timeline chart */}
                {sf && result.timeline_accumulo && result.timeline_accumulo.length > 1 && (
                  <div className="bg-white rounded-xl border border-[#E5E2DB] p-3" data-testid="timeline-chart">
                    <p className="text-[10px] uppercase tracking-wider text-[#163321] font-bold mb-2">Timeline accumulo montante (euro reali)</p>
                    <div style={{ width: "100%", height: 220 }}>
                      <ResponsiveContainer>
                        <AreaChart data={result.timeline_accumulo.map(p => ({
                          anno: eta + p.anno,
                          Versamenti: p.da_versamenti,
                          TFR: p.da_tfr,
                          Preesistente: p.da_preesistente,
                        }))}>
                          <CartesianGrid stroke="#E5E2DB" strokeDasharray="3 3" />
                          <XAxis dataKey="anno" tick={{ fontSize: 10, fill: "#4A5E51" }} />
                          <YAxis tickFormatter={fmtEurCompat} tick={{ fontSize: 10, fill: "#4A5E51" }} width={55} />
                          <RTooltip formatter={(v) => fmtEur(v)} labelFormatter={(l) => `Età ${l}`} />
                          <Legend wrapperStyle={{ fontSize: 10 }} />
                          <Area type="monotone" dataKey="Versamenti" stackId="1" stroke="#163321" fill="#163321" fillOpacity={0.8} />
                          <Area type="monotone" dataKey="TFR" stackId="1" stroke="#D97B58" fill="#D97B58" fillOpacity={0.8} />
                          <Area type="monotone" dataKey="Preesistente" stackId="1" stroke="#FFD089" fill="#FFD089" fillOpacity={0.8} />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                <p className="text-[10px] text-[#4A5E51]/70 leading-relaxed">{result.disclaimer}</p>

                {/* CTA consulenza */}
                <Link
                  to="/fondi-pensione/questionario"
                  className="block w-full text-center h-12 leading-[48px] rounded-full bg-[#163321] text-white font-semibold text-sm hover:bg-[#0F2F1F] transition"
                  data-testid="cta-consulenza"
                >
                  Prenota consulenza personalizzata <ArrowRight className="inline-block w-4 h-4 ml-1" />
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderField({ label, value, setValue, min, max, step = 1, unit, testid, hint }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <Label className="text-xs text-[#163321]/80">{label}</Label>
        <span className="text-base font-bold text-[#163321]">{(unit === "€") ? fmtEur(value) : `${value} ${unit}`}</span>
      </div>
      <Slider min={min} max={max} step={step} value={[value]} onValueChange={(v) => setValue(v[0])} data-testid={testid} />
    </div>
  );
}

function ResultBox({ value, label, color = "neutral" }) {
  const colors = {
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    neutral: "bg-white border-[#E5E2DB] text-[#163321]",
  };
  return (
    <div className={`rounded-xl p-3 border ${colors[color]}`}>
      <p className="text-lg font-bold">{value}</p>
      <p className="text-[10px] uppercase tracking-wider mt-0.5 opacity-70">{label}</p>
    </div>
  );
}

// =============================================================================
// PRODOTTI
// =============================================================================
function ProdottiSection({ products }) {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14" data-testid="prodotti-section">
      <h2 className="font-heading text-3xl text-[#163321] mb-2">Pensione Reale di Reale Mutua</h2>
      <p className="text-sm text-[#4A5E51] mb-8 max-w-2xl">Due Piani Individuali Pensionistici (PIP) iscritti all'albo COVIP. Scegli in base al tuo profilo: solo integrazione pensionistica oppure pensione + protezione completa.</p>

      <div className="grid md:grid-cols-2 gap-5">
        <ProdottoCard prodotto={products.classic} accent="#163321" />
        <ProdottoCard prodotto={products.premium} accent="#D97B58" />
      </div>
    </section>
  );
}

function ProdottoCard({ prodotto, accent }) {
  return (
    <div className="relative bg-white rounded-3xl p-6 sm:p-7 border-2 border-[#E5E2DB] hover:shadow-lg transition" data-testid={`prodotto-${prodotto.slug}`}>
      <span className="absolute -top-3 left-6 text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1 rounded-full" style={{ backgroundColor: accent }}>
        {prodotto.ltc ? "Con LTC obbligatoria" : "Senza LTC"}
      </span>
      <p className="text-[10px] uppercase tracking-widest text-[#4A5E51] font-semibold mb-1.5 mt-2">{prodotto.tipo}</p>
      <h3 className="font-heading text-2xl text-[#0F2F1F] mb-1">{prodotto.nome}</h3>
      <p className="text-sm font-medium mb-4" style={{ color: accent }}>{prodotto.headline}</p>

      <div className="space-y-2 text-xs text-[#4A5E51] mb-5">
        <Row label="Premio minimo" value={prodotto.premio_min} />
        <Row label="Età sottoscrizione" value={prodotto.eta_min ? `${prodotto.eta_min}-${prodotto.eta_max} anni` : "Senza limiti"} />
        <Row label="Linee di investimento" value={`${prodotto.comparti.length} comparti`} />
        <Row label="Garanzia LTC" value={prodotto.ltc ? "✓ Inclusa obbligatoria" : "Non prevista"} />
        <Row label="Caso morte / invalidità" value={prodotto.morte_invalidita_facoltativa ? "✓ Facoltativa" : "Non prevista"} />
      </div>

      <div className="bg-[#F5F4F0] rounded-xl p-3 mb-4">
        <p className="text-[10px] uppercase tracking-wider font-bold text-[#163321] mb-2">Comparti disponibili</p>
        <div className="space-y-1">
          {prodotto.comparti.map(c => (
            <div key={c.id} className="flex items-start justify-between text-[11px]">
              <span className="text-[#163321]">{c.nome}</span>
              <span className="text-[#4A5E51]/70 ml-2 whitespace-nowrap">Rischio {c.rischio}</span>
            </div>
          ))}
        </div>
      </div>

      <details className="text-[11px] text-[#4A5E51] mb-3" data-testid={`target-${prodotto.slug}`}>
        <summary className="cursor-pointer font-semibold text-[#163321] underline">Target market POG (per chi è adatto)</summary>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="bg-emerald-50 rounded-lg p-2 border border-emerald-200">
            <p className="text-[10px] font-bold text-emerald-800 uppercase mb-1">✓ Adatto a</p>
            <ul className="space-y-0.5">{prodotto.target_positivo.map((t, i) => <li key={i}>• {t}</li>)}</ul>
          </div>
          <div className="bg-red-50 rounded-lg p-2 border border-red-200">
            <p className="text-[10px] font-bold text-red-800 uppercase mb-1">✕ NON adatto a</p>
            <ul className="space-y-0.5">{prodotto.target_negativo.map((t, i) => <li key={i}>• {t}</li>)}</ul>
          </div>
        </div>
      </details>

      <Link to={`/fondi-pensione/questionario?prodotto=${prodotto.slug}`} className="block w-full text-center h-11 leading-[44px] rounded-full text-white font-semibold text-sm transition hover:opacity-90" style={{ backgroundColor: accent }} data-testid={`btn-richiedi-${prodotto.slug}`}>
        Richiedi quotazione
      </Link>
    </div>
  );
}

function Row({ label, value }) { return <div className="flex items-center justify-between"><span className="text-[#4A5E51]/70">{label}</span><span className="font-medium text-[#163321]">{value}</span></div>; }

// =============================================================================
// DOCUMENTI
// =============================================================================
function DocumentiSection({ docs }) {
  return (
    <section className="bg-[#FFF6EE] border-y border-[#E8D3A0]/50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12" data-testid="documenti-section">
        <h2 className="font-heading text-3xl text-[#163321] mb-2 flex items-center gap-2"><FileText className="w-7 h-7 text-[#D97B58]" /> Documentazione contrattuale</h2>
        <p className="text-sm text-[#4A5E51] mb-6 max-w-2xl">
          <strong>Prima di sottoscrivere è obbligatorio leggere</strong>: Comunicazione informativa al rischio (CIR), Nota Informativa, Regolamento del Fondo, Documento sul regime fiscale, Documento sulle rendite e sulle anticipazioni.
        </p>

        <div className="grid sm:grid-cols-3 gap-4">
          <DocGroup title="Documenti comuni" docs={docs.common} accent="#163321" />
          <DocGroup title="Pensione Reale Classic" docs={docs.classic} accent="#163321" />
          <DocGroup title="Pensione Reale Premium" docs={docs.premium} accent="#D97B58" />
        </div>
      </div>
    </section>
  );
}

function DocGroup({ title, docs, accent }) {
  const PREVIEW = 6;
  const [expanded, setExpanded] = useState(false);
  const list = expanded ? docs : docs.slice(0, PREVIEW);
  return (
    <div className="bg-white rounded-2xl p-4 border border-[#E5E2DB]">
      <p className="font-semibold text-[#163321] text-sm mb-3" style={{ color: accent }}>{title}</p>
      <ul className="space-y-1.5">
        {list.map(d => (
          <li key={d.key}>
            <a href={`${process.env.REACT_APP_BACKEND_URL}${d.url}`} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#4A5E51] hover:text-[#163321] flex items-start gap-1.5" data-testid={`doc-${d.key}`}>
              <Download className="w-3 h-3 mt-0.5 flex-shrink-0" /> {d.label}
            </a>
          </li>
        ))}
      </ul>
      {docs.length > PREVIEW && (
        <button onClick={() => setExpanded(e => !e)} className="text-[10px] mt-2 underline text-[#4A5E51]">
          {expanded ? "Mostra meno" : `+ Altri ${docs.length - PREVIEW} documenti`}
        </button>
      )}
    </div>
  );
}

// =============================================================================
// COMPLIANCE NOTICE
// =============================================================================
function ComplianceSection() {
  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 py-10" data-testid="compliance-section">
      <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 text-[11px] text-[#4A5E51] leading-relaxed space-y-2">
        <p className="font-semibold text-[#163321] text-xs">Trasparenza e conformità normativa</p>
        <p>
          <strong>Pensione Reale Classic</strong> e <strong>Pensione Reale Premium</strong> sono Piani Individuali Pensionistici (PIP) di tipo assicurativo emessi da <strong>Reale Mutua Assicurazioni</strong> (Compagnia di Assicurazione iscritta IVASS), conformi all'art. 13 co. 2 lett. b) D.Lgs. 5 dicembre 2005 n° 252.
        </p>
        <p>
          Distribuzione tramite <strong>Christian e Ciro Colucci S.A.S.</strong> — Intermediario assicurativo iscritto al RUI (Registro Unico Intermediari) sez. A — n° A000464752, in regime di consulenza, ai sensi del Reg. IVASS 40/2018.
        </p>
        <p>
          Il presente sito non costituisce sollecitazione al pubblico risparmio. Le adesioni avvengono in fase di consulenza presso la sede di Colucci Assicuratori (Sant'Antimo, NA), previa consegna del set documentale obbligatorio (CIR, Nota Informativa, Regolamento, Documento Anticipazioni, Documento Rendite, Documento Regime Fiscale).
        </p>
        <p>
          La <strong>deduzione fiscale</strong> dei contributi versati è regolata dall'art. 8 D.Lgs. 252/2005 — limite annuo <strong>€ 5.300,00</strong> come innalzato dalla Legge di Bilancio 2026 (cumulativo con altre forme di previdenza complementare). Per i lavoratori di prima occupazione successiva al 1/1/2007 è ammessa, nei primi 20 anni di adesione, una extra-deducibilità fino a <strong>€ 7.950,00/anno</strong> (art. 8 c.6 D.Lgs. 252/2005). Il TFR conferito al fondo pensione non rientra nel limite di deducibilità annuo. Il regime fiscale può variare in base alla normativa vigente.
        </p>
      </div>
    </section>
  );
}
